/**
 * Shows a fallback error UI when errors bypass React error boundaries
 * Uses vanilla JavaScript/DOM since React might be broken
 *
 * @param reactErrorCode - Optional React error code (e.g., "310")
 *
 * @example
 * // Test in browser console:
 * window.__testReactError310?.()
 */
export const showFallbackErrorUI = (reactErrorCode?: string) => {
  // Prevent showing multiple error overlays
  if (document.getElementById("fallback-error-overlay")) {
    return;
  }

  const overlay = document.createElement("div");
  overlay.id = "fallback-error-overlay";
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  `;

  const container = document.createElement("div");
  container.style.cssText = `
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 32px 40px;
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
  `;

  const title = document.createElement("h1");
  title.textContent = "Something Went Wrong!";
  title.style.cssText = `
    font-size: 30px;
    font-family: serif;
    color: #1f2937;
    font-weight: bold;
    margin-bottom: 16px;
    margin-top: 0;
  `;

  const message = document.createElement("p");
  message.textContent =
    "Don't worry, we've been notified and our team is already working diligently to resolve the issue. We appreciate your patience and will get back to you soon.";
  message.style.cssText = `
    color: #475569;
    margin-bottom: 32px;
    line-height: 1.5;
  `;

  const buttonContainer = document.createElement("div");
  buttonContainer.style.cssText = `
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  `;

  const refreshButton = document.createElement("button");
  refreshButton.textContent = "Refresh Page";
  refreshButton.style.cssText = `
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  `;
  refreshButton.onclick = () => window.location.reload();

  const homeButton = document.createElement("button");
  homeButton.textContent = "Go Home";
  homeButton.style.cssText = `
    background: #f1f5f9;
    color: #334155;
    border: none;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
  `;
  homeButton.onclick = () => window.location.replace("/");

  const errorInfo = document.createElement("p");
  errorInfo.style.cssText = `
    font-size: 12px;
    color: #94a3b8;
    margin-top: 16px;
    margin-bottom: 0;
  `;

  if (reactErrorCode) {
    const reactErrorLink = document.createElement("a");
    reactErrorLink.href = `https://react.dev/errors/${reactErrorCode}`;
    reactErrorLink.target = "_blank";
    reactErrorLink.textContent = `React Error #${reactErrorCode}`;
    reactErrorLink.style.cssText = `
      color: #3b82f6;
      text-decoration: underline;
    `;
    errorInfo.textContent = "Error details: ";
    errorInfo.appendChild(reactErrorLink);
  } else {
    errorInfo.textContent =
      "An unexpected error occurred. Please try refreshing the page.";
  }

  buttonContainer.appendChild(refreshButton);
  buttonContainer.appendChild(homeButton);

  container.appendChild(title);
  container.appendChild(message);
  container.appendChild(buttonContainer);
  container.appendChild(errorInfo);

  overlay.appendChild(container);
  document.body.appendChild(overlay);
};

// Expose test functions in development for testing error UI
if (typeof window !== "undefined") {
  // @ts-expect-error - Test utility for development
  window.__testReactError310 = () => {
    showFallbackErrorUI("310");
  };
  // @ts-expect-error - Test utility for development
  window.__testReactError = (code?: string) => {
    showFallbackErrorUI(code);
  };
  // @ts-expect-error - Test utility for development - simulates actual React error #310 event
  window.__simulateReactError310 = () => {
    const error = new Error(
      "Minified React error #310; visit https://react.dev/errors/310 for the full message or use the non-minified dev environment for full errors and additional helpful warnings.",
    );
    window.dispatchEvent(
      new ErrorEvent("error", {
        error,
        message: error.message,
        filename: "test.tsx",
        lineno: 1,
        colno: 1,
      }),
    );
  };
}
